import { num } from '@/ui/format';
/**
 * «Planlegg uke N» (#163), as the reviewer's prototype draws it (docs/design/planning-hugin-delivery):
 * the ways to plan the week, the three fill-gaps proposals as cards, the chosen one's summary, and
 * «Dette endres i uke N». The existing ways (copy, templates, plan yourself) are the page's own
 * actions, listed here as they were in the Planlegg menu. ChangeStrip is the blue bar above the grid.
 */
import { addDays, isoWeek } from '@timefly/core';

import { T } from '@/i18n';
import { openHugin, useDock } from '@/poc-chat/attach';
import { oslo, osloISO } from '@/rules';
import { HuginMark, Icon, type IconName } from '@/ui/icons';
import { useState } from 'react';
import { OptionRow } from './OptionRow';
import type { BasisLine, Lift, Preview } from './api';
import {
  type Assessment,
  type WarnGroup,
  basisLine,
  basisRows,
  beforeAfter,
  changesOf,
  compareOf,
  countsText,
  dayLabel,
  dayRanges,
  incompleteOf,
  kr,
  span,
} from './model';
import css from './planning.module.css';
import type { Planning } from './usePlanning';

export interface PlanItem {
  key: string;
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

const Row = ({
  icon,
  title,
  about,
  onClick,
  danger,
}: {
  icon: IconName;
  title: string;
  about?: string;
  onClick: () => void;
  danger?: boolean | undefined;
}) => (
  <button type="button" className={`${css.row}${danger ? ` ${css.danger}` : ''}`} onClick={onClick}>
    <Icon name={icon} />
    <span>
      <b>{title}</b>
      {about && <small>{about}</small>}
    </span>
    <Icon name="chevronRight" />
  </button>
);

export function PlanningPanel({
  plan,
  week,
  scope,
  copyItem,
  templateItems,
  typeName,
  onSelf,
  onClose,
  onShown,
  chatOn = false,
  huginTab = false,
}: {
  /** Vaktplan on a desktop for a person with the assistant: Hugin docks in this panel's second tab */
  huginTab?: boolean;
  /** Hugin can answer here: the person may use the assistant and the company is a demo (#163) */
  chatOn?: boolean;
  /** after «Vis valgt forslag»: on a phone the sheet closes so the plan with its marks shows */
  onShown?: (() => void) | undefined;
  plan: Planning;
  /** ISO week number */
  week: number;
  /** «Fjordlys Kafé · 19.–25. okt. · hele teamet» */
  scope: string;
  copyItem: PlanItem;
  templateItems: PlanItem[];
  typeName: (id: number | null) => string;
  onSelf: () => void;
  onClose: () => void;
}) {
  const { step, shown } = plan;
  const title =
    step === 'changes'
      ? T('Se endringene')
      : step === 'edit'
        ? T('Endre vakten i forslaget')
        : T('Planlegg uke {week}', { week });
  const picked = plan.options.find((o) => o.id === plan.picked) ?? null;
  const data = plan.data as Preview | null;
  const money = (m: number | null) => (m == null ? T('Lønn skjult') : kr(m));
  const basisName = data?.basis
    ? data.basis.kind === 'template'
      ? String(data.basis.ref.name ?? T('vaktmalen'))
      : data.basis.kind === 'need'
        ? T('bemanningsbehovet')
        : data.basis.kind === 'week' && typeof data.basis.ref.week_start === 'string'
          ? T('uke {n}', { n: isoWeek(data.basis.ref.week_start) })
          : T('tidligere uker')
    : null;

  let body: React.ReactNode;
  let foot: React.ReactNode = null;
  if (step === 'menu') {
    body = (
      <>
        <div className={css.intro}>
          <h3>{T('Hvordan vil du planlegge uka?')}</h3>
          <p>{T('Start der det passer. Du kan bytte måte underveis.')}</p>
        </div>
        {chatOn && (
          <Row
            icon="hugin"
            title={T('Skriv til Hugin')}
            about={T('Beskriv det du vil, eller still et spørsmål.')}
            onClick={() => openHugin()}
          />
        )}
        <Row
          icon="hugin"
          title={T('Få et planforslag')}
          about={T('Velg prioritering og få vaktene fordelt.')}
          onClick={plan.suggest}
        />
        <Row
          icon="copy"
          title={copyItem.label}
          about={T('Gjenbruk vaktene fra uka før.')}
          onClick={copyItem.onSelect}
        />
        {templateItems.map((it) => (
          <Row
            key={it.key}
            icon={it.danger ? 'trash' : 'calendar'}
            title={it.label}
            danger={it.danger}
            onClick={it.onSelect}
          />
        ))}
        <Row
          icon="pencil"
          title={T('Planlegg selv')}
          about={T('Legg til vakter selv i planen.')}
          onClick={onSelf}
        />
        <p className={css.note}>{T('Eksisterende vakter slettes ikke når du velger en måte å jobbe på.')}</p>
      </>
    );
  } else if (step === 'basis') {
    const b = plan.bases;
    const none = !b || (!b.need?.slots && !b.templates.length && !b.history.slots && !b.week?.slots);
    body = (
      <>
        <button type="button" className={css.back} onClick={() => plan.setStep('menu')}>
          <Icon name="chevronLeft" />
          {T('Alle måter å planlegge på')}
        </button>
        <div className={css.intro}>
          <h3>{T('Hva skal uka bygge på?')}</h3>
          <p data-testid={none ? 'plan-no-basis' : undefined}>
            {none
              ? T(
                  'Hugin trenger å vite hvilke vakter uke {week} skal ha. Det står ikke i en vaktmal, og de siste fire ukene gjentar seg ikke. Kopier en uke eller legg inn vaktene, så fordeler Hugin folk på dem.',
                  {
                    week,
                  },
                )
              : T(
                  'Uke {week} har ingen vakter. En tom uke sier ikke hvor mange som trengs, så velg et grunnlag og bekreft det før Timefly fordeler folk.',
                  { week },
                )}
          </p>
        </div>
        {none ? (
          <>
            {/* nothing to propose from (owner 18.10): no alarm, the useful way first; saving or deleting a
                template makes no sense on an empty week, so only «Bruk mal» rows are offered here */}
            <Row
              icon="copy"
              title={copyItem.label}
              about={T('Samme vakter som uka før. Du ser dem før noe lagres.')}
              onClick={copyItem.onSelect}
            />
            {templateItems
              .filter((it) => !it.danger && it.key !== 'save')
              .map((it) => (
                <Row key={it.key} icon="calendar" title={it.label} onClick={it.onSelect} />
              ))}
            <Row
              icon="pencil"
              title={T('Planlegg selv')}
              about={T('Legg til vakter selv i planen.')}
              onClick={onSelf}
            />
          </>
        ) : (
          <fieldset className={css.options} data-testid="plan-bases">
            <legend>{T('Velg grunnlag')}</legend>
            {/* the company's own staffing need, first (owner 18.10: the site sells «team and need in, week out») */}
            {b.need && b.need.slots > 0 && (
              <OptionRow
                name="plan-basis"
                value="need"
                checked={plan.basisPick === 'need'}
                onChange={() => plan.pickBasis('need')}
                title={T('Dekk bemanningsbehovet')}
                aside={T('{n} vakter', { n: b.need.slots })}
                testid="plan-basis-need"
              >
                <span>{T('Bedriftens vakttyper, dekker behovet time for time. Hugin velger hvem.')}</span>
                <BasisDetails
                  lines={b.need.lines}
                  missing={gapRows(b.need.left_out)}
                  missingLabel={T('Ikke dekket av en vakttype')}
                />
              </OptionRow>
            )}
            {b.templates.map((t) => (
              <OptionRow
                key={t.template_id}
                name="plan-basis"
                value={`template:${t.template_id}`}
                checked={plan.basisPick === `template:${t.template_id}`}
                onChange={() => plan.pickBasis(`template:${t.template_id}`)}
                title={T('Bruk vaktmal «{name}»', { name: t.name })}
                aside={T('{n} vakter', { n: t.slots })}
                testid="plan-basis-template"
              >
                <BasisDetails lines={t.lines} />
              </OptionRow>
            ))}
            {b.history.slots > 0 && (
              <OptionRow
                name="plan-basis"
                value="history"
                checked={plan.basisPick === 'history'}
                onChange={() => plan.pickBasis('history')}
                title={T('Som tidligere uker')}
                aside={T('{n} vakter', { n: b.history.slots })}
                testid="plan-basis-history"
              >
                <span>{T('Vakter som gikk igjen minst 3 av de 4 siste ukene.')}</span>
                <BasisDetails
                  lines={b.history.lines}
                  missing={b.history.left_out.map(
                    (x) => `${basisLine(x, x.weekday)} (${T('{n} av 4 uker', { n: x.weeks ?? 0 })})`,
                  )}
                  missingLabel={T('Ikke tatt med')}
                />
              </OptionRow>
            )}
            {/* the week before as it was, when no pattern repeats (owner 18.10: a full week 42 is enough to plan 43) */}
            {!b.history.slots && b.week && b.week.slots > 0 && b.week.week_start && (
              <OptionRow
                name="plan-basis"
                value="week"
                checked={plan.basisPick === 'week'}
                onChange={() => plan.pickBasis('week')}
                title={T('Som uke {n}', { n: isoWeek(b.week.week_start) })}
                aside={T('{n} vakter', { n: b.week.slots })}
                testid="plan-basis-week"
              >
                <span>{T('De samme vaktene, samme dager og tider. Hugin velger hvem.')}</span>
                <BasisDetails lines={b.week.lines} />
              </OptionRow>
            )}
          </fieldset>
        )}
      </>
    );
    foot = none ? null : (
      <>
        <button
          type="button"
          className={css.go}
          onClick={plan.confirmBasis}
          disabled={!plan.basisPick}
          data-testid="plan-basis-confirm"
        >
          <Icon name="check" />
          {T('Bruk dette grunnlaget')}
        </button>
        <small>
          {T('Grunnlaget lagres i forslaget. Ingen vakter lagres før du velger «Lagre som utkast».')}
        </small>
      </>
    );
  } else if (step === 'options') {
    body = (
      <>
        <button
          type="button"
          className={css.back}
          onClick={() => (plan.basis ? plan.suggest() : plan.setStep('menu'))}
        >
          <Icon name="chevronLeft" />
          {plan.basis ? T('Velg et annet grunnlag') : T('Alle måter å planlegge på')}
        </button>
        <div className={css.intro}>
          <h3>{T('Hvilket forslag passer best?')}</h3>
          <p>
            {data && basisName
              ? T('Samme {n} vakter i {basis}. Sammenlign lønn og fordeling før du velger.', {
                  n: data.context.demand.items.length,
                  basis: basisName,
                })
              : data
                ? T('{n} ledige vakter i uke {week}. Sammenlign lønn og fordeling før du velger.', {
                    n: data.context.demand.items.length,
                    week,
                  })
                : T('Sammenlign lønn og fordeling før du velger.')}
          </p>
        </div>
        {/* when the people's hours cannot cover the week, say so with the numbers (owner 18.10) */}
        {data?.capacity &&
          plan.options.length > 0 &&
          Math.max(...plan.options.map((o) => o.fills)) < (plan.options[0]?.open ?? 0) &&
          (() => {
            const chosen = plan.options.find((o) => o.id === plan.picked) ?? plan.options[0];
            const used = new Map((chosen?.proposal.hours_by_person ?? []).map((h) => [h.user_id, h.hours]));
            const name = (id: number) => {
              const n = data.context.people.find((x) => x.user_id === id)?.name.split(' ')[0] ?? '';
              return n ? n[0]?.toUpperCase() + n.slice(1) : '';
            };
            const left = data.capacity.people
              .map((x) => ({
                who: name(x.user_id),
                h: Math.round((x.free_hours - (used.get(x.user_id) ?? 0)) * 10) / 10,
              }))
              .filter((x) => x.h >= 1)
              .sort((a, b) => b.h - a.h);
            const open = (chosen?.open ?? 0) - (chosen?.fills ?? 0);
            return (
              <p className={css.common} data-testid="plan-capacity">
                <Icon name="info" />
                <span>
                  {T(
                    'Behovet er {need} t. De ansatte kan jobbe {free} t til etter kontraktene, så {n} vakter står ledige.',
                    {
                      need: num(data.capacity.need_hours),
                      free: num(data.capacity.free_hours),
                      n: open,
                    },
                  )}
                  {left.length > 0 &&
                    ` ${T('Timer igjen: {list}.', { list: left.map((x) => `${x.who} ${num(x.h)} t`).join(', ') })}`}
                </span>
              </p>
            );
          })()}
        <LimitsNote plan={plan} />
        {plan.loading && <p className={css.loading}>{T('Lager forslag …')}</p>}
        {plan.error && (
          <p className={css.problem} role="alert">
            {T(plan.error)}
          </p>
        )}
        {plan.error && !plan.loading && (
          <button type="button" className={css.link} onClick={plan.suggest}>
            <Icon name="refresh" />
            {T('Prøv igjen')}
          </button>
        )}
        {plan.options.length > 0 && (
          <>
            {/* the prototype's comparison (planning.js compareBody): one radio row per option, three lines
                at most; warnings as a count, the details of the chosen one and what all share said once */}
            <fieldset className={css.options} data-testid="plan-options">
              <legend>
                {data?.basis?.kind === 'template'
                  ? T('Samme vaktmal. Ulik fordeling.')
                  : data?.basis?.kind === 'history'
                    ? T('Samme mønster som tidligere uker. Ulik fordeling.')
                    : data?.basis
                      ? T('Samme vakter. Ulik fordeling.')
                      : T('Samme ledige vakter. Ulik fordeling.')}
              </legend>
              {plan.options.map((o, i) => (
                <OptionRow
                  key={o.id}
                  name="plan-option"
                  value={o.id}
                  checked={plan.picked === o.id}
                  onChange={() => plan.pick(o.id)}
                  title={
                    <>
                      {o.label}
                      {o.warnings.length > 0 && (
                        <span className={css.countChip} data-testid="plan-warning-count">
                          {o.warnings.length === 1
                            ? T('1 nytt varsel')
                            : T('{n} nye varsler', { n: o.warnings.length })}
                        </span>
                      )}
                    </>
                  }
                  aside={money(o.totalMinor)}
                  testid={`plan-option-${o.objective}`}
                >
                  <span className={css.delta}>
                    {[
                      o.overMinor == null
                        ? null
                        : o.overMinor === 0
                          ? i > 0 && o.totalMinor === plan.options[0]?.totalMinor
                            ? T('samme kostnad')
                            : T('laveste kostnad')
                          : T('{kr} mer enn billigste', { kr: kr(o.overMinor) }),
                      T('fyller {n} av {m}', { n: o.fills, m: o.open }),
                      // what the cards cannot show by price and fill alone (owner 18.10: three alike)
                      i > 0 && !o.sameAs && o.differs > 0
                        ? T('{n} vakter til andre enn i «{first}»', {
                            n: o.differs,
                            first: plan.options[0]?.label ?? '',
                          })
                        : null,
                      o.weekend.length ? T('helg: {who}', { who: o.weekend.join(', ') }) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    <br />
                    {o.sameAs ? T('Samme som «{name}».', { name: o.sameAs }) : o.about}
                  </span>
                </OptionRow>
              ))}
            </fieldset>
            {(() => {
              const cmp = compareOf(plan.options);
              const chosen = plan.options.find((o) => o.id === plan.picked) ?? null;
              const own = chosen ? cmp.own(chosen) : [];
              return (
                <>
                  {cmp.common.length > 0 && (
                    <p className={css.common} data-testid="plan-common">
                      <Icon name="info" />
                      <span>
                        {T('Gjelder alle forslagene: ufullstendig fordi {why}.', {
                          why: cmp.common.join(T(' og ')),
                        })}
                      </span>
                    </p>
                  )}
                  {chosen && (chosen.warnings.length > 0 || own.length > 0 || chosen.existing > 0) && (
                    <div className={css.compareNote} data-testid="plan-compare-note">
                      {chosen.warnings.length > 0 ? (
                        <>
                          <b>
                            {chosen.label}:{' '}
                            {chosen.warnings.length === 1
                              ? T('1 nytt varsel')
                              : T('{n} nye varsler', { n: chosen.warnings.length })}
                          </b>
                          <WarnList groups={chosen.byPerson} />
                        </>
                      ) : (
                        <p className={css.noneNew}>
                          {T('{label} gir ingen nye varsler.', { label: chosen.label })}
                        </p>
                      )}
                      {/* what the saved week already said: not this option's doing, so a count, not a list (owner 18.10) */}
                      {chosen.existing > 0 && (
                        <p data-testid="plan-existing">
                          {chosen.existing === 1
                            ? T('1 varsel fantes fra før i uka. Se dem i Gjennomgå.')
                            : T('{n} varsler fantes fra før i uka. Se dem i Gjennomgå.', {
                                n: chosen.existing,
                              })}
                        </p>
                      )}
                      {own.length > 0 && <p>{`${T('Ufullstendig fordi')} ${own.join(T(' og '))}.`}</p>}
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}
        {chatOn && plan.options.length > 0 && <Instruction plan={plan} />}
        {plan.options.length > 0 && (
          <p className={css.estimate}>
            {T(
              'Anslått lønn med feriepenger, pensjon og arbeidsgiveravgift for vaktene som har en person. Tillegg og overtid er ikke medregnet.',
            )}
          </p>
        )}
      </>
    );
    foot = picked ? (
      <>
        <div className={css.footSum}>
          <span>{picked.label}</span>
          <b>{money(picked.totalMinor)}</b>
        </div>
        <button
          type="button"
          className={css.go}
          onClick={() => {
            plan.show();
            onShown?.();
          }}
          data-testid="plan-show"
        >
          <Icon name="hugin" />
          {T('Vis valgt forslag')}
        </button>
        <small>{T('Viser endringene før du lagrer.')}</small>
      </>
    ) : null;
  } else if (shown && data && plan.proposal) {
    const p = plan.proposal;
    const ba = beforeAfter(data.baseline_cost, p.cost);
    const total = p.cost.visibility === 'visible' ? (p.cost.total_minor ?? null) : null;
    const open = plan.assessment.filter((a) => a.state === 'open');
    const toReview = open.filter((a) => a.severity === 'warning');
    const toFix = open.filter((a) => a.severity === 'danger');
    const done = plan.assessment.filter((a) => a.state === 'acknowledged');
    const label = `${T('Forslag')}: ${shown.label}${plan.version > 1 ? ` · ${T('justert')}` : ''}`;
    const save = (
      <>
        {plan.error && (
          <p className={css.problem} role="alert">
            {T(plan.error)}
          </p>
        )}
        {/* a way out when the week changed under the proposal (owner 18.10: «impossible to exit») */}
        {plan.error && (
          <div className={css.rowActions} data-testid="plan-stale-actions">
            <button type="button" className={css.link} onClick={plan.suggest}>
              <Icon name="refresh" />
              {T('Hent nytt forslag')}
            </button>
            <button type="button" className={css.link} onClick={plan.discard} data-testid="plan-discard">
              <Icon name="close" />
              {T('Forkast forslaget')}
            </button>
          </div>
        )}
        <div className={css.footSum}>
          <span>{label}</span>
          <b>{money(total)}</b>
        </div>
        <button
          type="button"
          className={css.go}
          onClick={plan.save}
          disabled={plan.saving}
          data-testid="plan-save"
        >
          <Icon name="save" />
          {plan.saving ? T('Lagrer …') : T('Lagre som utkast')}
        </button>
        <small>
          {open.length
            ? T('Lagrer versjon {v} med {n} åpne varsler. Ingenting publiseres.', {
                v: plan.version,
                n: open.length,
              })
            : T('Lagrer dette forslaget. Ingenting publiseres.')}
        </small>
      </>
    );
    foot = save;
    if (step === 'edit' && plan.editing) {
      body = <EditShift plan={plan} people={data.context.people} typeName={typeName} />;
      foot = null;
    } else if (step === 'changes') {
      const rows = changesOf(p, data, typeName);
      body = (
        <>
          <button type="button" className={css.back} onClick={() => plan.setStep('chosen')}>
            <Icon name="chevronLeft" />
            {T('Til forslaget')}
          </button>
          <div className={css.intro}>
            <h3>{T('Dette endres i uke {week}', { week })}</h3>
            <p>{T('Sammenlignet med den lagrede planen.')}</p>
          </div>
          <dl className={css.facts} data-testid="plan-facts">
            {ba.pay && (
              <div>
                <dt>{T('Anslått lønn')}</dt>
                <dd>{ba.pay}</dd>
              </div>
            )}
            <div>
              <dt>{T('Planlagte timer')}</dt>
              <dd>{ba.hours}</dd>
            </div>
          </dl>
          <div className={css.diffList} data-testid="plan-changes">
            {rows.length ? (
              rows.map((r) => (
                <div className={css.diffRow} key={r.key}>
                  <b>
                    {r.title}
                    <span>{r.mark === 'new' ? T('Ny') : T('Endret')}</span>
                  </b>
                  <div>
                    <small>{T('Før')}</small>
                    {r.before}
                  </div>
                  <div>
                    <small>{T('Etter')}</small>
                    {r.after}
                  </div>
                </div>
              ))
            ) : (
              <p className={css.note}>{T('Ingen vakter er endret.')}</p>
            )}
          </div>
          <p className={css.note}>{T('Endringene lagres først når du velger «Lagre som utkast».')}</p>
          <button type="button" className={css.link} onClick={plan.discard}>
            {T('Forkast forslaget')}
          </button>
        </>
      );
    } else {
      body = (
        <>
          <button type="button" className={css.back} onClick={() => plan.setStep('options')}>
            <Icon name="chevronLeft" />
            {T('Alle forslagene')}
          </button>
          <section className={css.summary} aria-label={T('Oppsummering av forslaget')}>
            <div className={css.summaryTitle}>
              <b>{label}</b>
              <span className={css.pill}>
                <Icon name="clock" />
                {T('Versjon {v} · ikke lagret', { v: plan.version })}
              </span>
            </div>
            <dl className={css.numbers}>
              <div>
                <dt>{T('Anslått lønn')}</dt>
                <dd>{money(total)}</dd>
              </div>
              <div>
                <dt>{T('{n} vakter fylt', { n: p.coverage.covered })}</dt>
                <dd>
                  {(p.cost.hours ?? 0).toLocaleString('nb-NO')} <span>{T('timer')}</span>
                </dd>
              </div>
            </dl>
            {ba.payDelta != null && (
              <p className={css.costChange}>
                {ba.payDelta === 0
                  ? T('Samme lønnsanslag som lagret uke.')
                  : ba.payDelta > 0
                    ? T('{kr} høyere enn lagret uke.', { kr: kr(ba.payDelta) })
                    : T('{kr} lavere enn lagret uke.', { kr: kr(-ba.payDelta) })}
              </p>
            )}
            <small className={css.scope}>
              {T('Lønn, feriepenger, pensjon og arbeidsgiveravgift · tillegg og overtid ikke medregnet.')}
            </small>
          </section>
          <button type="button" className={css.link} onClick={() => plan.setStep('changes')}>
            <Icon name="eye" />
            {countsText(p) || T('Ingen endringer')} · {T('se endringer')}
          </button>
          {plan.assessment.length > 0 && <Review plan={plan} toReview={toReview} toFix={toFix} done={done} />}
          {incompleteOf(p) && (
            <p className={css.problem} data-testid="plan-incomplete">
              {incompleteOf(p)}
            </p>
          )}
          <p className={css.note}>
            {chatOn
              ? T('Velg en vakt i planen for å endre den, eller be Hugin justere forslaget.')
              : T('Velg en vakt i planen for å endre den. Publisering er et eget steg.')}
          </p>
          <div className={css.pair}>
            {chatOn && (
              <button
                type="button"
                className={css.pairBtn}
                onClick={() => {
                  plan.adjustWithHugin();
                  // on a phone the sheet steps aside for the chat
                  onShown?.();
                }}
                data-testid="plan-adjust-hugin"
              >
                <HuginMark size={20} />
                {T('Juster med Hugin')}
              </button>
            )}
            <button
              type="button"
              className={css.pairBtn}
              onClick={() => {
                const first = p.operations.findIndex((o) => o.after);
                const op = p.operations[first];
                if (op) plan.beginEdit(op.target.shift_id ?? -(first + 1));
              }}
            >
              <Icon name="pencil" />
              {T('Rediger selv')}
            </button>
          </div>
          <button type="button" className={css.link} onClick={plan.discard}>
            {T('Forkast forslaget')}
          </button>
        </>
      );
    }
  } else {
    body = null;
  }

  return (
    <aside className={`vpanel ${css.panel}`} aria-label={title} data-testid="plan-panel">
      <div className={`vphd ${css.head}`}>
        <div>
          <h2>{title}</h2>
          <p>{scope}</p>
        </div>
        <button type="button" className={css.close} aria-label={T('Lukk')} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      {huginTab && (
        <div className={css.tabs} role="tablist" aria-label={T('Planlegg eller Hugin')}>
          <button
            type="button"
            role="tab"
            aria-selected={plan.tab === 'plan'}
            onClick={() => plan.setTab('plan')}
          >
            <Icon name="calendar" />
            {T('Planlegg')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={plan.tab === 'hugin'}
            onClick={() => plan.setTab('hugin')}
            data-testid="plan-tab-hugin"
          >
            <HuginMark size={20} />
            {T('Hugin')}
          </button>
        </div>
      )}
      {huginTab && plan.tab === 'hugin' ? (
        // the one Hugin conversation (poc-chat PocChatPanel) is drawn here while this tab shows
        <div
          className={css.dock}
          ref={(el) => useDock.getState().set({ target: el })}
          data-testid="plan-hugin-dock"
        />
      ) : (
        <>
          <div className={css.body}>{body}</div>
          {foot && <div className={css.foot}>{foot}</div>}
        </>
      )}
    </aside>
  );
}

/**
 * What stopped the chosen option from filling every shift, and «Vil du fortsette likevel?» with the
 * limits the manager may lift: contract hours and availability. Absence, rest and the law stay (owner 18.10).
 */
function LimitsNote({ plan }: { plan: Planning }) {
  const [pick, setPick] = useState<Lift[]>([]);
  const l = plan.data?.limits?.[plan.picked ?? ''] ?? null;
  const lifted = plan.lift;
  if (plan.loading || !l) return null;
  const open = l.contract + l.availability + l.contract_and_availability + l.law;
  const can: Lift[] = (['contract', 'availability'] as Lift[]).filter(
    (k) => !lifted.includes(k) && (l[k] > 0 || l.contract_and_availability > 0),
  );
  if (!open && !lifted.length) return null;
  const rows = [
    [l.contract, T('{n} på grunn av kontraktstimene', { n: l.contract })],
    [l.availability, T('{n} på grunn av tilgjengeligheten', { n: l.availability })],
    [l.contract_and_availability, T('{n} på grunn av begge', { n: l.contract_and_availability })],
    [
      l.law,
      T('{n} på grunn av fravær, hviletid eller arbeidstidsreglene, som ikke kan overstyres', { n: l.law }),
    ],
  ].filter(([n]) => Number(n) > 0) as [number, string][];
  const label: Record<Lift, string> = {
    contract: T('Gå over kontraktstimene'),
    availability: T('Se bort fra tilgjengeligheten'),
  };
  return (
    <div className={css.limits} data-testid="plan-limits">
      {open > 0 && (
        <>
          <strong>{T('{n} vakter kan ikke fylles innenfor grensene', { n: open })}</strong>
          <ul>
            {rows.map(([, text]) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </>
      )}
      {lifted.length > 0 && (
        <p>
          {T('Tillatt i dette forslaget: {list}. Hver ansatt det gjelder har en advarsel.', {
            list: lifted.map((k) => label[k].toLowerCase()).join(', '),
          })}{' '}
          <button type="button" className={css.link} onClick={() => plan.liftLimits([])}>
            {T('Tilbake til grensene')}
          </button>
        </p>
      )}
      {can.length > 0 && (
        <fieldset>
          <legend>{T('Vil du fortsette likevel?')}</legend>
          {can.map((k) => (
            <label key={k}>
              <input
                type="checkbox"
                checked={pick.includes(k)}
                onChange={(e) => setPick((p) => (e.target.checked ? [...p, k] : p.filter((x) => x !== k)))}
              />
              {label[k]}
            </label>
          ))}
          <button
            type="button"
            className={css.go}
            disabled={!pick.length}
            onClick={() => {
              plan.liftLimits([...lifted, ...pick]);
              setPick([]);
            }}
          >
            {T('Fortsett likevel')}
          </button>
        </fieldset>
      )}
    </div>
  );
}

/** The blue bar above the grid while a proposal is shown, and the receipt once it is saved. */
export function ChangeStrip({
  plan,
  week,
  onChanges,
}: { plan: Planning; week: number; onChanges: () => void }) {
  if (plan.saved && !plan.shown) {
    return (
      <output className={`${css.strip} ${css.saved}`} data-testid="plan-strip">
        <div>
          <strong>{T('Lagret som utkast')}</strong>
          <small>
            {T('Uke {week} · {n} vakter lagret', { week, n: plan.saved.n })}
            {plan.saved.publishedOpen
              ? ` · ${T('{n} av dem var allerede publisert som ledige', { n: plan.saved.publishedOpen })}`
              : ''}{' '}
            · {T('publisering er et eget steg')}
          </small>
        </div>
        <button type="button" className={css.stripLink} onClick={plan.dismissSaved}>
          <Icon name="close" />
          {T('Lukk')}
        </button>
      </output>
    );
  }
  if (!plan.shown) return null;
  return (
    <div className={css.strip} data-testid="plan-strip">
      <div>
        <strong>{(plan.proposal && countsText(plan.proposal)) || T('Ingen endringer')}</strong>
        <small>
          {plan.data?.baseline.shifts.length === 0
            ? T('Mot tom uke · ikke lagret')
            : T('Mot lagret uke · ikke lagret')}
        </small>
      </div>
      <fieldset className={css.switch}>
        <legend className="sr">{T('Vis planen før eller etter')}</legend>
        <button type="button" aria-pressed={plan.side === 'before'} onClick={() => plan.setSide('before')}>
          {T('Før')}
        </button>
        <button type="button" aria-pressed={plan.side === 'after'} onClick={() => plan.setSide('after')}>
          {T('Etter')}
        </button>
      </fieldset>
      <button type="button" className={css.stripLink} onClick={onChanges} data-testid="plan-see-changes">
        <Icon name="eye" />
        {T('Se endringer')}
      </button>
      {/* save from the strip too, handy from the Hugin tab when the plan is right (owner 18.10) */}
      <button
        type="button"
        className={`${css.stripLink} ${css.stripSave}`}
        onClick={() => void plan.save()}
        disabled={plan.saving || !plan.proposal}
        data-testid="plan-strip-save"
      >
        <Icon name="save" />
        {plan.saving ? T('Lagrer …') : T('Lagre som utkast')}
      </button>
      {/* leave planning: the proposal goes, the saved week stays as it is (owner 18.10) */}
      <button
        type="button"
        className={css.stripLink}
        onClick={plan.discard}
        data-testid="plan-strip-discard"
        aria-label={T('Forkast forslaget')}
        title={T('Forkast forslaget. Den lagrede uka er uendret.')}
      >
        <Icon name="close" />
        {T('Forkast')}
      </button>
    </div>
  );
}

export { css as planningCss };

/**
 * The prototype's «Vaktmal · Tildelte vakter» row under the grid while a week planned from a basis is
 * shown: per day, the basis slots given a person in the view («Etter» or «Før») out of the slots.
 */
export function BasisRow({
  plan,
  days,
  style,
}: { plan: Planning; days: string[]; style?: React.CSSProperties }) {
  const b = plan.data?.basis;
  if (!b || !plan.shown) return null;
  const creates =
    plan.side === 'after' && plan.proposal
      ? plan.proposal.operations.filter((o) => o.kind === 'create' && o.after)
      : [];
  const hours = (d: string) =>
    creates
      .filter((o) => o.after?.user_id != null && oslo(o.after.start_time)?.date === d)
      .reduce(
        (h, o) => h + (Date.parse(o.after?.end_time ?? '') - Date.parse(o.after?.start_time ?? '')) / 36e5,
        0,
      );
  return (
    <div className="grow totals" style={style} data-testid="plan-basis-row">
      <div>
        <span className="lab">
          {b.kind === 'template'
            ? T('Vaktmal')
            : b.kind === 'need'
              ? T('Bemanningsbehov')
              : b.kind === 'week'
                ? T('Som uka før')
                : T('Tidligere uker')}
        </span>
        <span className="sub">{T('Tildelte vakter')}</span>
      </div>
      {days.map((d) => {
        const need = b.lines.filter((l) => l.date === d).reduce((n, l) => n + l.count, 0);
        const given = creates.filter(
          (o) => o.after?.user_id != null && oslo(o.after.start_time)?.date === d,
        ).length;
        return (
          <div key={d} data-testid="plan-basis-day">
            <b>
              {given} / {need}
            </b>
            <span className="sub">
              {T('{h} t planlagt', { h: hours(d).toLocaleString('nb-NO', { maximumFractionDigits: 1 }) })}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * «N må vurderes» (prototype desktop-preview): the proposal's findings, the warnings to acknowledge with
 * a reason, the dangers to fix, what the manager already assessed. An acknowledgement holds only for
 * the exact facts; a changed shift opens it again («Endret siden vurdert»).
 */
function Review({
  plan,
  toReview,
  toFix,
  done,
}: { plan: Planning; toReview: Assessment[]; toFix: Assessment[]; done: Assessment[] }) {
  const [openList, setOpenList] = useState(false);
  const [asking, setAsking] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const count = toReview.length + toFix.length;
  return (
    <div className={css.warnBox} data-testid="plan-review">
      <b>
        <Icon name="warn" />
        {count ? T('{n} må vurderes', { n: count }) : T('Alt er vurdert')}
      </b>
      <span className={css.reviewSub}>
        {T('Gjeldende forslag · versjon {v}', { v: plan.version })}
        {done.length ? ` · ${T('{n} vurdert av deg', { n: done.length })}` : ''}
      </span>
      <button
        type="button"
        className={css.reviewToggle}
        aria-expanded={openList}
        onClick={() => setOpenList(!openList)}
      >
        <Icon name="chevronRight" />
        {openList ? T('Skjul') : T('Se over')}
      </button>
      {openList && (
        <div className={css.reviewList}>
          {toFix.map((a) => (
            <div key={a.key} className={css.reviewItem} data-testid="plan-finding" data-state="fix">
              <span>
                <em>{T('Må rettes')}</em> {a.text}
              </span>
              <small>{T('Endre vakten i planen. Dette kan ikke bare markeres som vurdert.')}</small>
            </div>
          ))}
          {toReview.map((a) => (
            <div key={a.key} className={css.reviewItem} data-testid="plan-finding" data-state="open">
              <span>
                <em>{a.reopened ? T('Endret siden vurdert') : T('Må vurderes')}</em> {a.text}
              </span>
              {asking === a.key ? (
                <span className={css.ackForm}>
                  <input
                    className="inp"
                    aria-label={T('Hvorfor er dette i orden?')}
                    placeholder={T('Hvorfor er dette i orden?')}
                    value={reason}
                    maxLength={500}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <button
                    type="button"
                    className={css.link}
                    disabled={reason.trim().length < 3}
                    onClick={() => {
                      plan.acknowledge(a.ids, reason);
                      setAsking(null);
                      setReason('');
                    }}
                  >
                    {T('Marker som vurdert')}
                  </button>
                </span>
              ) : (
                <button type="button" className={css.link} onClick={() => setAsking(a.key)}>
                  {T('Vurder')}
                </button>
              )}
            </div>
          ))}
          {done.map((a) => (
            <div key={a.key} className={css.reviewItem} data-testid="plan-finding" data-state="acknowledged">
              <span>
                <em>{T('Vurdert av deg')}</em> {a.text}
                {a.reason ? ` · «${a.reason}»` : ''}
              </span>
              <button type="button" className={css.link} onClick={() => plan.reopen(a.ids)}>
                {T('Åpne igjen')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Person and same-day times of one shift in the proposal; the server makes the new version. */
function EditShift({
  plan,
  people,
  typeName,
}: { plan: Planning; people: { user_id: number; name: string }[]; typeName: (id: number | null) => string }) {
  const e = plan.editing as NonNullable<Planning['editing']>;
  const date = oslo(e.start_time)?.date ?? '';
  const [who, setWho] = useState<string>(e.user_id == null ? '' : String(e.user_id));
  const [from, setFrom] = useState(oslo(e.start_time)?.time ?? '');
  const [to, setTo] = useState(oslo(e.end_time)?.time ?? '');
  const op = plan.proposal?.operations.find((o) =>
    e.target.shift_id != null
      ? o.target.shift_id === e.target.shift_id
      : o.target.client_id === e.target.client_id,
  );
  return (
    <>
      <button type="button" className={css.back} onClick={plan.cancelEdit}>
        <Icon name="chevronLeft" />
        {T('Til forslaget')}
      </button>
      <div className={css.intro}>
        <h3>
          {dayLabel(date)} · {typeName(op?.after?.shift_type_id ?? null)}
        </h3>
        <p>
          {T('Nå i forslaget')}: {people.find((x) => x.user_id === e.user_id)?.name ?? T('Ledig vakt')} ·{' '}
          {span(e.start_time, e.end_time)}
        </p>
      </div>
      <label className={css.field}>
        <span>{T('Ansatt')}</span>
        <select
          className="inp"
          aria-label={T('Ansatt i forslaget')}
          value={who}
          onChange={(x) => setWho(x.target.value)}
        >
          <option value="">{T('Ledig vakt')}</option>
          {people.map((x) => (
            <option key={x.user_id} value={x.user_id}>
              {x.name}
            </option>
          ))}
        </select>
      </label>
      <div className={css.twoFields}>
        <label className={css.field}>
          <span>{T('Fra')}</span>
          <input
            className="inp"
            type="time"
            aria-label={T('Fra i forslaget')}
            value={from}
            onChange={(x) => setFrom(x.target.value)}
          />
        </label>
        <label className={css.field}>
          <span>{T('Til')}</span>
          <input
            className="inp"
            type="time"
            aria-label={T('Til i forslaget')}
            value={to}
            onChange={(x) => setTo(x.target.value)}
          />
        </label>
      </div>
      {plan.error && (
        <p className={css.problem} role="alert">
          {T(plan.error)}
        </p>
      )}
      <button
        type="button"
        className={css.go}
        disabled={plan.revising || !from || !to}
        data-testid="plan-edit-save"
        onClick={() =>
          plan.submitEdit({
            user_id: who ? Number(who) : null,
            start_time: osloISO(date, from),
            // an end at or before the start is the next day's, as an evening to 00:00 is (owner 18.10)
            end_time: osloISO(to <= from ? addDays(date, 1) : date, to),
          })
        }
      >
        <Icon name="check" />
        {plan.revising ? T('Regner ut …') : T('Oppdater forslaget')}
      </button>
      <p className={css.note}>
        {T('Lønn, timer og varsler regnes ut på nytt. Endringen lagres først med «Lagre som utkast».')}
      </p>
    </>
  );
}

/** The chosen option's warnings by person, most first, each rule once with its days; four people until asked */
function WarnList({ groups }: { groups: WarnGroup[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? groups : groups.slice(0, 4);
  return (
    <>
      <ul className={css.warnList} data-testid="plan-warn-list">
        {shown.map((g) => (
          <li key={g.who}>
            <b>{g.who}</b> <span className={css.warnN}>{g.n}</span>
            {g.items.map((x) => (
              <span key={x.text} className={css.warnItem}>
                {x.text}
                <small> · {x.days.join(', ')}</small>
              </span>
            ))}
          </li>
        ))}
      </ul>
      {groups.length > 4 && (
        <button type="button" className="link" onClick={() => setAll(!all)}>
          {all ? T('Vis færre') : T('Vis alle {n} personer', { n: groups.length })}
        </button>
      )}
    </>
  );
}

/**
 * The comparison's «Juster med Hugin» (owner 18.10): it opens Hugin's chat on the proposal on the grid,
 * where the manager writes; a second small input here only lost what was typed in it.
 */
function Instruction({ plan }: { plan: Planning }) {
  return (
    <button
      type="button"
      className={css.link}
      data-testid="plan-instruction"
      disabled={!plan.shown}
      onClick={() => plan.adjustWithHugin()}
    >
      <HuginMark size={20} />
      {T('Juster med Hugin')}
    </button>
  );
}

/**
 * A basis's shifts, closed by default (owner 18.10: the text under the title was a wall): a toggle with
 * the count on the right, and opened a short list, one row per type and time with its days as chips.
 * A button, not <details>, because the row is a <label>: a click on it must not pick the radio.
 */
function BasisDetails({
  lines,
  missing = [],
  missingLabel,
}: { lines: BasisLine[]; missing?: string[]; missingLabel?: string }) {
  const [open, setOpen] = useState(false);
  const rows = basisRows(lines);
  return (
    <span className={css.basisMore}>
      <button
        type="button"
        className={css.basisToggle}
        aria-expanded={open}
        data-testid="plan-basis-toggle"
        onClick={(e) => {
          e.preventDefault();
          setOpen(!open);
        }}
      >
        <span>{open ? T('Skjul vaktene') : T('Se vaktene')}</span>
        <span className={css.basisCount}>
          {rows.length === 1 ? T('1 type') : T('{n} typer', { n: rows.length })}
          {missing.length > 0 && ` · ${T('{n} hull', { n: missing.length })}`}
          <Icon name="chevronDown" />
        </span>
      </button>
      {open && (
        <span className={css.basisList} data-testid="plan-basis-rows">
          {rows.map((r) => (
            <span key={r.key} className={css.basisItem}>
              <b>{r.type}</b>
              <span className={css.basisTime}>{r.time}</span>
              <span className={css.basisDays}>
                {r.days.map((d) => (
                  <i key={d}>{d}</i>
                ))}
              </span>
              {r.perDay > 1 && <em>×{r.perDay}</em>}
            </span>
          ))}
          {missing.length > 0 && (
            <span className={css.basisGaps}>
              <b>{missingLabel}</b>
              {missing.map((m) => (
                <span key={m}>{m}</span>
              ))}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/** The need's uncovered hours, one line per time and people with their days as runs: «07:00–08:00 · alle dager · 1 pers.» */
function gapRows(left: { weekday?: number; from?: string; to?: string; people?: number }[]): string[] {
  const by = new Map<string, number[]>();
  for (const x of left) {
    const k = `${x.from}–${x.to}|${x.people ?? 1}`;
    by.set(k, [...(by.get(k) ?? []), (x.weekday ?? 1) - 1]);
  }
  return [...by.entries()].map(([k, days]) => {
    const [time, people] = k.split('|');
    return `${time} · ${dayRanges([...new Set(days)].sort((a, b) => a - b)).join(', ')} · ${T('{n} pers.', { n: Number(people) })}`;
  });
}
